import type { IgfdPaymentPlan, Restaurant } from "api-types";
import type { PersistConfig } from "redux-persist/es/types";
import storage from "redux-persist/lib/storage";

import { BasicPlan } from "@dashboard/environment/plans";
import type {
  ReduxState,
  RestaurantDraft,
  RestaurantWithPaymentPlan,
} from "@dashboard/types";

// defaults to localStorage for web

export type RestaurantSelectedAction = {
  type: "RESTAURANT_SELECTED";
  payload: Restaurant | null; // null if no restaurant is selected
};
// Actions for modifications on the Restaurant entity
export type RestaurantReducerAction =
  | RestaurantSelectedAction // When the current restaurant is selected / updated from the header.
  | { type: "FETCHED_RESTAURANTS"; payload: RestaurantWithPaymentPlan[] } // All restaurants are fetched from the API.
  | {
      type: "UPDATE_PAYMENT_PLAN";
      payload: {
        restaurantUuid: string;
        paymentPlan: IgfdPaymentPlan;
      };
    } // A payment Plan for a restaurant is updated.
  | { type: "RESTAURANT_CREATED"; payload: Restaurant } // New restaurant is created.
  | { type: "RESTAURANT_DETAILS_UPDATED"; payload: Partial<RestaurantDraft> }; // Restaurant details are updated (price range, website, etc.)

/** If the state is persisted to local storage, this is the config used for that persist. */
// We don't persist during storybook.
export const restaurantReducerPersistConfig = {
  key: "igfd-dashboard-restaurant",
  storage,
  whitelist: ["selectedRestaurant"],
} as PersistConfig<ReduxState["restaurant"]>;

export const restaurantReducer: (
  state: ReduxState["restaurant"] | undefined,
  action: RestaurantReducerAction
) => ReduxState["restaurant"] = (
  state: ReduxState["restaurant"] = {
    initialRestaurantsLoading: true,
    accessibleRestaurants: [],
    selectedRestaurant: null,
    paymentPlansByRestaurantUuid: {} as {
      [restaurantUuid: string]: IgfdPaymentPlan;
    },
  },
  action: RestaurantReducerAction
) => {
  switch (action.type) {
    case "FETCHED_RESTAURANTS":
      // When all restaurants are fetched from the API.
      const paymentPlansByRestaurantUuid: {
        [restaurantUuid: string]: IgfdPaymentPlan;
      } = action.payload.reduce(
        (acc, restaurantWithPaymentPlan) => ({
          ...acc,
          [restaurantWithPaymentPlan.restaurant.uuid]:
            restaurantWithPaymentPlan.paymentPlan,
        }),
        {}
      );

      return {
        ...state,
        initialRestaurantsLoading: false,
        accessibleRestaurants: action.payload.map(
          (restaurantWithPaymentPlan) => restaurantWithPaymentPlan.restaurant
        ),
        paymentPlansByRestaurantUuid: paymentPlansByRestaurantUuid,
      } as ReduxState["restaurant"];

    case "UPDATE_PAYMENT_PLAN":
      // When a payment plan for a restaurant is updated.
      return {
        ...state,
        paymentPlansByRestaurantUuid: {
          ...state.paymentPlansByRestaurantUuid,
          [action.payload.restaurantUuid]: action.payload.paymentPlan,
        },
      } as ReduxState["restaurant"];

    case "RESTAURANT_CREATED":
      // When a new restaurant is created.
      return {
        ...state,
        accessibleRestaurants: [...state.accessibleRestaurants, action.payload],
        // set the newly created restaurant as the selected restaurant
        selectedRestaurant: action.payload,
        paymentPlansByRestaurantUuid: {
          ...state.paymentPlansByRestaurantUuid,
          // Newly created Restaurant is always on the Basic Plan.
          // Later on they will upgrade to a paid plan, after adding a subscription.
          [action.payload.uuid]: BasicPlan,
        },
      } as ReduxState["restaurant"];
    case "RESTAURANT_SELECTED":
      // When the current restaurant is selected / updated from the header or initial laod.
      // If no restaurant exists, this is also handled here.
      return {
        ...state,
        selectedRestaurant: action.payload,
      } as ReduxState["restaurant"];
    case "RESTAURANT_DETAILS_UPDATED":
      // a partial set of fields may have been updated.
      // Here we merge the updated fields with the existing restaurant.
      const updatedRestaurant = mergeDraft(
        action.payload,
        state.selectedRestaurant as Restaurant
      );
      return {
        ...state,
        selectedRestaurant: updatedRestaurant,
      } as ReduxState["restaurant"];
    default:
      return state;
  }
};

// Apply the draft to the restaurant.
const mergeDraft: (
  draft: Partial<RestaurantDraft>,
  restaurant: Restaurant
) => Restaurant = (draft: Partial<RestaurantDraft>, restaurant: Restaurant) => {
  return {
    ...restaurant,
    ...draft,
    address: {
      ...restaurant.address,
      ...draft.address,
    },
  } as Restaurant;
};
